/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { FiAlertTriangle, FiCopy, FiMinus, FiPlus, FiThermometer, FiTrash2 } from "react-icons/fi";
import { LINE_STATUS, SLOTS, STATUS_STYLES } from "../constants";
import { foodRelationLabel } from "../helpers";
import MedicinePicker from "./MedicinePicker";
import RxCrop from "./RxCrop";

const Stepper = ({ label, value, min, onChange, suffix }) => (
    <div className="flex items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-[0.08em] text-cod-gray-500">{label}</span>
        <div className="flex items-center rounded-lg border border-cod-gray-200 bg-white-50">
            <button
                type="button"
                aria-label={`Decrease ${label}`}
                onClick={() => onChange(Math.max(min, value - 1))}
                className="p-2 text-cod-gray-400 hover:text-cod-gray-900"
            >
                <FiMinus size={14} />
            </button>
            <span className="min-w-[2.75rem] text-center text-sm font-semibold tabular-nums text-cod-gray-900">
                {value}
                {suffix ? <span className="text-xs font-normal text-cod-gray-500">{suffix}</span> : null}
            </span>
            <button
                type="button"
                aria-label={`Increase ${label}`}
                onClick={() => onChange(value + 1)}
                className="p-2 text-cod-gray-400 hover:text-cod-gray-900"
            >
                <FiPlus size={14} />
            </button>
        </div>
    </div>
);

const DoseGrid = ({ schedule }) => {
    const ariaLabel = SLOTS.map((s) => `${s.label}: ${Number(schedule?.[s.key]) || 0}`).join(", ");

    return (
        <div className="flex items-center gap-1" role="img" aria-label={ariaLabel}>
            {SLOTS.map((slot) => {
                const dose = Number(schedule?.[slot.key]) || 0;

                return (
                    <span
                        key={slot.key}
                        aria-hidden="true"
                        className={`flex h-6 w-6 items-center justify-center rounded text-xs font-semibold tabular-nums ${dose > 0 ? "bg-cod-gray-900 text-white-50" : "bg-cod-gray-100 text-cod-gray-400"
                            }`}
                    >
                        {dose > 0 ? dose : slot.short}
                    </span>
                );
            })}
        </div>
    );
};

const Flag = ({ icon: Icon, children, tone }) => (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-semibold uppercase tracking-[0.06em] ${tone}`}>
        <Icon size={11} />
        {children}
    </span>
);

const MedicineLine = ({ line, isActive, onSelect, onChange, onResolve, onRemove }) => {
    const [isPicking, setIsPicking] = useState(false);
    const status = STATUS_STYLES[line.status];
    const needsAttention = line.status !== LINE_STATUS.MATCHED;
    const label = line.name || line.rawName;

    const handleCardClick = () => {
        onSelect(line.id);
        // For lines that need attention and have no crop to highlight, opening
        // the picker immediately makes the click feel purposeful.
        if (needsAttention && !isPicking) setIsPicking(true);
    };

    return (
        <li data-line-id={line.id}>
            <div
                role="button"
                tabIndex={0}
                onClick={handleCardClick}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCardClick();
                    }
                }}
                className={`relative flex cursor-pointer gap-0 overflow-hidden rounded-xl border bg-white-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-elm-400 ${isActive ? "border-cod-gray-300 shadow-soft-xs" : "border-cod-gray-100 hover:border-cod-gray-200"
                    }`}
            >
                <span aria-hidden="true" className={`w-1 shrink-0 ${status.rail}`} />

                <div className="flex min-w-0 flex-1 flex-col gap-3 p-3.5">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="truncate text-[15px] font-semibold leading-snug text-cod-gray-900">
                                {line.name || line.rawName}
                            </p>
                            <p className="mt-0.5 truncate text-xs text-cod-gray-500">
                                {line.name
                                    ? [
                                        line.detail,
                                        line.addedByHand ? "Added by hand" : `read as "${line.rawName}"`,
                                    ].filter(Boolean).join(" · ")
                                    : status.label}
                            </p>
                        </div>

                        <div
                            className="flex shrink-0 items-center gap-3"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <label className="flex cursor-pointer items-center gap-2">
                                <span className="text-xs font-medium text-cod-gray-500">Include</span>
                                <input
                                    type="checkbox"
                                    checked={line.included}
                                    disabled={line.status !== LINE_STATUS.MATCHED}
                                    onChange={(e) => onChange({ ...line, included: e.target.checked })}
                                    className="h-6 w-6 cursor-pointer rounded border-cod-gray-300 text-elm-600 focus:ring-elm-400 disabled:cursor-not-allowed disabled:bg-cod-gray-100"
                                />
                            </label>

                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onRemove(line.id); }}
                                aria-label={`Remove ${label}`}
                                title="Remove this medicine"
                                className="rounded p-1.5 text-cod-gray-300 transition-colors hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                            >
                                <FiTrash2 size={15} />
                            </button>
                        </div>
                    </div>

                    {(line.isScheduleH || line.isColdStorage || line.isDuplicate) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            {line.isDuplicate && (
                                <Flag icon={FiCopy} tone="border-violet-200 bg-violet-50 text-violet-700">
                                    Duplicate
                                </Flag>
                            )}
                            {line.isScheduleH && (
                                <Flag icon={FiAlertTriangle} tone="border-amber-200 bg-amber-50 text-amber-700">
                                    Schedule H
                                </Flag>
                            )}
                            {line.isColdStorage && (
                                <Flag icon={FiThermometer} tone="border-cloud-burst-200 bg-cloud-burst-50 text-cloud-burst-700">
                                    Cold chain
                                </Flag>
                            )}
                        </div>
                    )}

                    {line.boundingBox && (
                        <RxCrop imageUrl={line.prescriptionImageUrl} boundingBox={line.boundingBox} />
                    )}

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <DoseGrid schedule={line.routine.schedule} />
                        <span className="text-xs text-cod-gray-500">
                            {foodRelationLabel(line.routine.relationToFood)}
                        </span>
                    </div>

                    {line.status === LINE_STATUS.MATCHED && (
                        <div
                            className="flex flex-wrap items-center gap-4 border-t border-cod-gray-100 pt-3"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Stepper
                                label="Days"
                                value={line.routine.duration}
                                min={1}
                                onChange={(duration) => onChange({ ...line, routine: { ...line.routine, duration } })}
                            />
                            <Stepper
                                label="Qty"
                                value={line.quantity}
                                min={1}
                                onChange={(quantity) => onChange({ ...line, quantity })}
                            />
                            {line.quantityNeedsReview && (
                                <span className="text-xs font-medium text-amber-700">Check this quantity</span>
                            )}
                            <button
                                type="button"
                                onClick={() => setIsPicking((prev) => !prev)}
                                className="ml-auto text-xs font-semibold text-elm-600 hover:text-elm-700"
                            >
                                Change medicine
                            </button>
                        </div>
                    )}

                    {(needsAttention || isPicking) && (
                        <div onClick={(e) => e.stopPropagation()}>
                            {!isPicking && needsAttention ? (
                                <button
                                    type="button"
                                    onClick={() => setIsPicking(true)}
                                    className={`w-full rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${line.status === LINE_STATUS.CHOOSE
                                        ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                        : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                        }`}
                                >
                                    {line.status === LINE_STATUS.CHOOSE
                                        ? `Pick from ${line.suggestions.length} close matches`
                                        : "Find this medicine"}
                                </button>
                            ) : (
                                <MedicinePicker
                                    suggestions={line.suggestions}
                                    onPick={(pick) => {
                                        onResolve(pick);
                                        setIsPicking(false);
                                    }}
                                    onCancel={() => setIsPicking(false)}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
};

export default MedicineLine;
